import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, map, Observable, of, Subject, take } from 'rxjs';
import { AccountService } from '../../account/account.service';
import { UpdateOwnProfileRequest, UserProfileDTO } from '../../models/user-profile.model';
import { ProfilbildService } from '../../services/profilbild.service';
import { requiredNotBlank } from '../../shared/forms/required-not-blank.validator';
import { warnBeforeUnload } from '../../shared/forms/warn-before-unload';
import { oberflaechenSprache } from '../../shared/oberflaechen-sprache';
import { AktiveSitzungenComponent } from '../aktive-sitzungen/aktive-sitzungen.component';
import { AvatarComponent } from '../avatar/avatar.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { SectionTitleComponent } from '../section-title/section-title.component';
import { kilobyte, megabyte, pruefeProfilbild, zuschneiden } from './profil-bild.util';

// Backend-Prüfung im UserService (Länge von first_name/last_name).
const NAME_MAX = 100;
const SPRACHEN: readonly string[] = ['de', 'en'];
// Eigenbezeichnung, auch für Sprachen, die nur per Einladung gespeichert sein können.
const SPRACHNAMEN: Readonly<Record<string, string>> = { de: 'Deutsch', en: 'English', fr: 'Français', es: 'Español' };
const ZEITZONEN: readonly string[] = ['Europe/Berlin', 'Europe/Vienna', 'UTC'];
const STANDARD_SPRACHE = 'de';
const STANDARD_ZEITZONE = 'Europe/Berlin';

type Feld = keyof UpdateOwnProfileRequest;

interface Sprachoption {
  code: string;
  label: string;
}

type Bildaenderung =
  | { art: 'neu'; blob: Blob; vorschau: string; dateiname: string; kb: number }
  | { art: 'entfernen' }
  | null;

interface Bildfehler {
  schluessel: string;
  params?: Record<string, string>;
}

type Teilergebnis = 'ok' | 'feldfehler' | 'fehler';

function hatDateien(ereignis: DragEvent): boolean {
  return Array.from(ereignis.dataTransfer?.types ?? []).includes('Files');
}

// Ein gespeicherter Wert außerhalb der Liste bleibt wählbar, sonst änderte schon das Öffnen ihn still.
function mitGespeichertemWert(optionen: readonly string[], gespeichert: string): string[] {
  return optionen.includes(gespeichert) ? [...optionen] : [...optionen, gespeichert];
}

function gespeicherteWerte(profil: UserProfileDTO): UpdateOwnProfileRequest {
  return {
    firstName: (profil.firstName ?? '').trim(),
    lastName: (profil.lastName ?? '').trim(),
    language: profil.language || STANDARD_SPRACHE,
    timezone: profil.timezone || STANDARD_ZEITZONE,
  };
}

@Component({
  selector: 'app-profil-bearbeiten',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    AktiveSitzungenComponent,
    AvatarComponent,
    ConfirmDialogComponent,
    SectionTitleComponent,
  ],
  templateUrl: './profil-bearbeiten.component.html',
  styleUrl: './profil-bearbeiten.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfilBearbeitenComponent implements OnInit {
  private readonly accountService = inject(AccountService);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly injector = inject(Injector);
  private readonly profilbild = inject(ProfilbildService);

  readonly nameMax = NAME_MAX;
  readonly profil = signal<UserProfileDTO | null>(null);
  readonly ladefehler = signal(false);
  readonly speichert = signal(false);
  readonly rueckfrage = signal(false);
  private readonly rueckfrageAntwort$ = new Subject<boolean>();

  readonly bildAenderung = signal<Bildaenderung>(null);
  readonly bildFehler = signal<Bildfehler | null>(null);
  readonly bestaetigung = signal(false);
  readonly zieht = signal(false);
  readonly verarbeitet = signal(false);
  private auswahlNummer = 0;

  private readonly entfernenKnopf = viewChild<ElementRef<HTMLButtonElement>>('entfernenKnopf');
  private readonly hochladenKnopf = viewChild<ElementRef<HTMLButtonElement>>('hochladenKnopf');
  private readonly abbrechenKnopf = viewChild<ElementRef<HTMLButtonElement>>('abbrechenKnopf');

  readonly form = this.fb.group({
    firstName: this.fb.control('', [requiredNotBlank, Validators.maxLength(NAME_MAX)]),
    lastName: this.fb.control('', [requiredNotBlank, Validators.maxLength(NAME_MAX)]),
    language: this.fb.control(STANDARD_SPRACHE),
    timezone: this.fb.control(STANDARD_ZEITZONE),
  });
  private readonly formEreignis = toSignal(this.form.events, { initialValue: null });
  private readonly gespeichert = computed(() => {
    const profil = this.profil();
    return profil ? gespeicherteWerte(profil) : null;
  });

  readonly gespeichertesBild = computed(() =>
    this.profilbild.benutzerId() === this.profil()?.id ? this.profilbild.url() : null,
  );
  readonly neueDatei = computed(() => {
    const aenderung = this.bildAenderung();
    return aenderung?.art === 'neu' ? aenderung : null;
  });
  readonly vorschau = computed(() => {
    if (this.bildAenderung()?.art === 'entfernen') return null;
    return this.neueDatei()?.vorschau ?? this.gespeichertesBild();
  });
  readonly hatBild = computed(() => this.vorschau() !== null);
  readonly bildGesperrt = computed(() => {
    this.formEreignis();
    return this.form.disabled || this.verarbeitet() || this.speichert();
  });
  readonly email = computed(() => this.profil()?.email ?? '');
  readonly sprachOptionen = computed<Sprachoption[]>(() =>
    mitGespeichertemWert(SPRACHEN, this.gespeichert()?.language ?? STANDARD_SPRACHE).map((code) => ({
      code,
      label: SPRACHNAMEN[code] ?? code,
    })),
  );
  readonly zeitzonen = computed(() =>
    mitGespeichertemWert(ZEITZONEN, this.gespeichert()?.timezone ?? STANDARD_ZEITZONE),
  );

  private readonly stammdatenGeaendert = computed(() => {
    this.formEreignis();
    const gespeichert = this.gespeichert();
    if (!gespeichert) return false;
    const aktuell = this.anfrage();
    return (Object.keys(aktuell) as Feld[]).some((feld) => aktuell[feld] !== gespeichert[feld]);
  });

  readonly hasUnsavedChanges = computed(() => this.stammdatenGeaendert() || this.bildAenderung() !== null);

  readonly kannSpeichern = computed(() => {
    this.formEreignis();
    return this.hasUnsavedChanges() && this.form.valid && !this.speichert() && !this.verarbeitet();
  });

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges());
    this.destroyRef.onDestroy(() => {
      this.auswahlNummer++;
      this.gibVorschauFrei(this.bildAenderung());
    });
  }

  ngOnInit(): void {
    this.laden();
  }

  laden(): void {
    this.ladefehler.set(false);
    this.form.disable();
    this.accountService
      .getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profil) => {
          this.profil.set(profil);
          this.form.reset(gespeicherteWerte(profil));
          this.form.enable();
          this.profilbild.laden(profil);
        },
        error: () => this.ladefehler.set(true),
      });
  }

  fehlerSchluessel(control: AbstractControl): string {
    return control.hasError('required') ? 'profilBearbeiten.pflichtfeld' : 'profilBearbeiten.feldUngueltig';
  }

  zumKonto(): void {
    void this.router.navigate(['/account']);
  }

  speichern(): void {
    if (!this.kannSpeichern()) return;
    const teile: Observable<Teilergebnis>[] = [];
    if (this.stammdatenGeaendert()) teile.push(this.speichereStammdaten(this.anfrage()));
    const bild = this.bildAenderung();
    if (bild) teile.push(this.speichereBild(bild));
    this.speichert.set(true);
    forkJoin(teile)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ergebnisse) => {
        this.speichert.set(false);
        if (ergebnisse.every((ergebnis) => ergebnis === 'ok')) {
          this.snackBar.open(this.translate.instant('profilBearbeiten.gespeichert'), undefined, {
            panelClass: 'snackbar-success',
          });
        } else if (ergebnisse.includes('fehler')) {
          this.snackBar.open(this.translate.instant('profilBearbeiten.speichernFehler'), undefined, {
            panelClass: 'snackbar-error',
          });
        }
      });
  }

  dateiGewaehlt(ereignis: Event): void {
    const input = ereignis.target as HTMLInputElement;
    const datei = input.files?.[0];
    input.value = '';
    if (datei && !this.bildGesperrt()) void this.verarbeite(datei);
  }

  ziehenBeginnt(ereignis: DragEvent): void {
    if (!hatDateien(ereignis)) return;
    ereignis.preventDefault();
    if (!this.bildGesperrt()) this.zieht.set(true);
  }

  ziehenUeber(ereignis: DragEvent): void {
    if (hatDateien(ereignis)) ereignis.preventDefault();
  }

  ziehenEndet(ereignis: DragEvent): void {
    const ziel = ereignis.relatedTarget as Node | null;
    if (ziel && (ereignis.currentTarget as HTMLElement).contains(ziel)) return;
    this.zieht.set(false);
  }

  loslassen(ereignis: DragEvent): void {
    if (!hatDateien(ereignis)) return;
    ereignis.preventDefault();
    this.zieht.set(false);
    const datei = ereignis.dataTransfer?.files[0];
    if (datei && !this.bildGesperrt()) void this.verarbeite(datei);
  }

  entfernenFragen(): void {
    this.bestaetigung.set(true);
    this.fokussiere(this.abbrechenKnopf);
  }

  entfernenAbbrechen(): void {
    this.bestaetigung.set(false);
    this.fokussiere(this.entfernenKnopf);
  }

  entfernenBestaetigen(): void {
    this.bestaetigung.set(false);
    this.setzeBildAenderung(this.gespeichertesBild() ? { art: 'entfernen' } : null);
    this.fokussiere(this.hochladenKnopf);
  }

  confirmDeactivate(): Observable<boolean> {
    this.rueckfrage.set(true);
    return this.rueckfrageAntwort$.pipe(take(1));
  }

  rueckfrageBeantworten(verlassen: boolean): void {
    this.rueckfrage.set(false);
    this.rueckfrageAntwort$.next(verlassen);
  }

  private async verarbeite(datei: File): Promise<void> {
    const nummer = ++this.auswahlNummer;
    this.bildFehler.set(null);
    this.bestaetigung.set(false);
    const pruefung = pruefeProfilbild(datei);
    if (pruefung === 'typ') {
      this.bildFehler.set({ schluessel: 'profilBearbeiten.profilbild.fehlerTyp' });
      return;
    }
    if (pruefung === 'groesse') {
      this.bildFehler.set({
        schluessel: 'profilBearbeiten.profilbild.fehlerGroesse',
        params: { mb: this.mb(datei.size) },
      });
      return;
    }
    this.verarbeitet.set(true);
    try {
      const blob = await zuschneiden(datei);
      if (nummer !== this.auswahlNummer) return;
      this.setzeBildAenderung({
        art: 'neu',
        blob,
        vorschau: URL.createObjectURL(blob),
        dateiname: datei.name,
        kb: kilobyte(datei.size),
      });
    } catch {
      if (nummer === this.auswahlNummer)
        this.bildFehler.set({ schluessel: 'profilBearbeiten.profilbild.fehlerLesbar' });
    } finally {
      if (nummer === this.auswahlNummer) this.verarbeitet.set(false);
    }
  }

  private mb(bytes: number): string {
    return new Intl.NumberFormat(this.translate.getCurrentLang() || 'de', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(megabyte(bytes));
  }

  private setzeBildAenderung(aenderung: Bildaenderung): void {
    this.gibVorschauFrei(this.bildAenderung());
    this.bildAenderung.set(aenderung);
  }

  private gibVorschauFrei(aenderung: Bildaenderung): void {
    if (aenderung?.art === 'neu') URL.revokeObjectURL(aenderung.vorschau);
  }

  private fokussiere(knopf: () => ElementRef<HTMLButtonElement> | undefined): void {
    afterNextRender(() => knopf()?.nativeElement.focus(), { injector: this.injector });
  }

  private speichereStammdaten(gesendet: UpdateOwnProfileRequest): Observable<Teilergebnis> {
    return this.accountService.updateProfile(gesendet).pipe(
      map((profil): Teilergebnis => {
        this.uebernehme(profil, gesendet);
        this.translate.use(oberflaechenSprache(profil.language));
        return 'ok';
      }),
      catchError((fehler: HttpErrorResponse) =>
        of<Teilergebnis>(this.zeigeFeldfehler(fehler) ? 'feldfehler' : 'fehler'),
      ),
    );
  }

  private speichereBild(aenderung: NonNullable<Bildaenderung>): Observable<Teilergebnis> {
    const anfrage = aenderung.art === 'neu' ? this.profilbild.speichern(aenderung.blob) : this.profilbild.entfernen();
    return anfrage.pipe(
      map((): Teilergebnis => {
        this.setzeBildAenderung(null);
        this.bildFehler.set(null);
        return 'ok';
      }),
      catchError(() => of<Teilergebnis>('fehler')),
    );
  }

  private anfrage(): UpdateOwnProfileRequest {
    const { firstName, lastName, language, timezone } = this.form.getRawValue();
    return { firstName: firstName.trim(), lastName: lastName.trim(), language, timezone };
  }

  // Was während des Speicherns schon wieder geändert wurde, bleibt als ungespeicherte Eingabe stehen.
  private uebernehme(profil: UserProfileDTO, gesendet: UpdateOwnProfileRequest): void {
    const aktuell = this.anfrage();
    const gespeichert = gespeicherteWerte(profil);
    this.profil.set(profil);
    for (const feld of Object.keys(gesendet) as Feld[]) {
      if (aktuell[feld] === gesendet[feld]) {
        this.form.controls[feld].setValue(gespeichert[feld]);
      }
    }
    this.form.markAsPristine();
  }

  private zeigeFeldfehler(fehler: HttpErrorResponse): boolean {
    const antwort = fehler.error as { code?: string; field?: string } | null;
    const feld = fehler.status === 400 ? antwort?.field : undefined;
    if (!feld || !(feld in this.form.controls)) return false;
    const control = this.form.controls[feld as Feld];
    control.setErrors(antwort?.code === 'REQUIRED' ? { required: true } : { abgelehnt: true });
    control.markAsTouched();
    return true;
  }
}
